/**
 * RLS denial logger
 *
 * Registra no backend (tabela `rls_denial_log`) toda vez que uma operação
 * em uma tabela sensível é negada pelo Row Level Security. Suporte/admin
 * usam isso para investigar pedidos de usuários ("não consigo ver X").
 *
 * - Só envia para tabelas listadas em SENSITIVE_TABLES (evita ruído).
 * - O backend já aplica rate limit de 1 registro por (user, table, op) / 60s.
 * - Falhas no próprio log nunca propagam (best-effort).
 */
import { supabase } from "@/integrations/supabase/client";
import type { PostgrestError } from "@supabase/supabase-js";

export type RlsOperation = "select" | "insert" | "update" | "delete" | "rpc" | "realtime";

// Tabelas sensíveis cujos acessos negados devem ser auditados.
// Mantenha em sincronia com a memória de segurança e com as colunas REVOKE.
const SENSITIVE_TABLES = new Set<string>([
  "profiles",
  "venues",
  "user_locations",
  "matches",
  "messages",
  "likes",
  "user_keys",
  "user_subscriptions",
  "user_coins",
  "coin_transactions",
  "mfa_recovery_codes",
  "age_verifications",
  "venue_kyc_attempts",
  "admin_audit_logs",
  "rls_denial_log",
  "fiscal_receipts",
  "blacklist_minors",
  "consent_logs",
  "terms_acceptances",
  "user_audit_log",
  "suspicious_activity",
]);

// Debounce client-side: evita N chamadas idênticas em curto espaço quando o
// componente re-renderiza após o erro. Backend tem rate limit próprio.
const recentLogs = new Map<string, number>();
const CLIENT_DEBOUNCE_MS = 30_000;

function isDeniedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as Partial<PostgrestError> & { status?: number; statusCode?: number };
  // Postgres: 42501 = insufficient_privilege; PostgREST: PGRST301 = RLS check failed; HTTP 401/403
  if (e.code === "42501" || e.code === "PGRST301") return true;
  const status = e.status ?? e.statusCode;
  if (status === 401 || status === 403) return true;
  const msg = String((e as { message?: string }).message ?? "").toLowerCase();
  return msg.includes("row-level security") || msg.includes("permission denied");
}

function shouldSkip(key: string): boolean {
  const now = Date.now();
  const last = recentLogs.get(key) ?? 0;
  if (now - last < CLIENT_DEBOUNCE_MS) return true;
  recentLogs.set(key, now);
  // Best-effort cleanup
  if (recentLogs.size > 200) {
    for (const [k, t] of recentLogs) {
      if (now - t > CLIENT_DEBOUNCE_MS * 2) recentLogs.delete(k);
    }
  }
  return false;
}

/**
 * Registra (best-effort) uma negação de RLS no backend.
 * Use quando capturar um erro de uma operação Supabase em tabela sensível.
 */
export async function logRlsDenial(
  table: string,
  operation: RlsOperation,
  error: unknown,
  payload?: Record<string, unknown>,
): Promise<void> {
  try {
    // Aceita tabelas sensíveis listadas + pseudo-nomes para RPC/Realtime (prefixados).
    if (!SENSITIVE_TABLES.has(table) && !table.startsWith("rpc:") && !table.startsWith("realtime:")) return;
    if (!isDeniedError(error)) return;
    if (shouldSkip(`${table}:${operation}`)) return;

    const code = (error as { code?: string; status?: number; statusCode?: number })?.code
      ?? String((error as { status?: number; statusCode?: number })?.status
        ?? (error as { statusCode?: number })?.statusCode
        ?? "");

    await supabase.rpc("log_rls_denial", {
      _table: table,
      _op: operation,
      _error_code: code || null,
      _payload: payload ? (payload as never) : null,
    });
  } catch {
    // Auditoria é best-effort — nunca quebra o fluxo do usuário.
  }
}

/**
 * Wrapper de conveniência: executa a promise e, em caso de erro de RLS,
 * registra a negação automaticamente antes de re-lançar/retornar.
 */
export async function withRlsDenialLogging<T>(
  table: string,
  operation: RlsOperation,
  run: () => Promise<{ data: T | null; error: unknown }>,
  payload?: Record<string, unknown>,
): Promise<{ data: T | null; error: unknown }> {
  const result = await run();
  if (result.error) {
    void logRlsDenial(table, operation, result.error, payload);
  }
  return result;
}
